import { batch } from "react-redux";
import moment from "moment";
import { type AnyAction } from "redux";
import { SALE_TRANSACTION_FINISHED } from "../../_Bootstrap/GlobalConstants";
import { type ShopfrontDispatch } from "../../_Bootstrap/hooks";
import { store } from "../../_Bootstrap/RoutingAndStore";
import PopupManager from "../../Common/Popups/PopupManager";
import { getEnterpriseAuthToken } from "../../Data/_Bases/BaseEnterpriseRepository";
import { type LocalDatabaseProduct } from "../../Data/_Bases/BaseProductRepository";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import {
    type SaleGiftCard,
    type SaleProduct,
    type SaleSurcharge,
} from "../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { RequestAmount } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestAmount";
import { RequestCode } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestCode";
import { RequestExpiry } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestExpiry";
import { RequestGiftCardSource } from "../../Routes/SellScreen/Dialogs/GiftCard/RequestGiftCardSource";
import { RequestPrice } from "../../Routes/SellScreen/Dialogs/Payments/RequestPrice";
import { RequestQuantity } from "../../Routes/SellScreen/Dialogs/RequestQuantity";
import RequestWeight from "../../Routes/SellScreen/Dialogs/RequestWeight";
import { PromotionAgent } from "../../Utilities/Domain/Promotions/PromotionAgent";
import { Permissions } from "../../Utilities/Domain/Users/Permissions";
import { type ShopfrontThunk } from "../../Utilities/Generic/ActionsHelper";
import { uppercaseFirst } from "../../Utilities/Generic/Strings";
import UUID from "../../Utilities/Generic/UUID";
import { ApplicationEventsFromShopfront } from "../../Utilities/Platform/Applications/ApplicationEvents";
import { getApplicationManager } from "../../Utilities/Platform/Applications/ApplicationUtilities";
import { weigh } from "../../Utilities/Platform/Hardware/Hardware";
import { getLogLevel, LogLevel } from "../../Utilities/Platform/Logging";
import { getOption } from "../../Utilities/Platform/Options/Options";
import { getPolicy } from "../../Utilities/Platform/Policies/Policies";
import { getMainHost, getVendorSubdomain } from "../../Utilities/Platform/Request";
import { Subscription } from "../../Utilities/Platform/Subscription/Subscription";
import { sendAction, sendSecurityEvent } from "../../Workers/Upload/UploadAgent";
import { showDialog, type ShowDialogAction } from "../Dialogs/DialogActions";
import { startLoading, stopLoading } from "../General/LayoutActions";
import { NoopAction, type NoopActionType } from "../General/MiscActions";
import {
    type AddingIntegratedProductToSale,
    addRefundReason,
    changeActiveProductIndex,
    changeSellScreenActionMode,
    changeSellScreenSearch,
    changeSellScreenSummaryMode,
    clearSale,
    type MarkProductAsAddingToSale,
    type ProductUpdateInterface,
    type SellScreenActionTypes,
    setNextSearchAction,
    setSaleSurchargingRules,
} from "./SellScreenActions";
import {
    ADD_INTEGRATED_PRODUCT_TO_SALE,
    MARK_PRODUCT_AS_ADDING,
    SALE_ADD_GIFTCARD,
    SALE_ADD_PRODUCT,
    SALE_CHANGE_QUANTITY,
    SALE_DISCOUNT_PRODUCT,
    SALE_HIDE_PRODUCT_DETAILS,
    SALE_REMOVE_PRODUCT,
    SALE_REQUEST_ASSOCIATE_BARCODE,
    SALE_SHOW_PRODUCT_DETAILS,
    SALE_TOGGLE_CASE,
    SALE_UNLOCK_PRODUCT,
    SALE_UPDATE_PRODUCTS,
} from "./SellScreenConstants";
import { type SaleAddPaymentOptions } from "./SellScreenPaymentActions";

export interface SaleAddProductOptions {
    quantity: number | "auto" | "request";
    barcodeQuantity: number | "auto";
    price: number | "auto" | "request";
    weight: number | boolean | "auto" | "request";
    consolidate: boolean | "auto";
    isCase: boolean | "auto";
    indexAddress: Array<number>;
    returnToSale: boolean;
    deleted: boolean;
    edited: boolean;
    metaData: Record<string, unknown>;
    lockQuantity: boolean;
}

export interface AddProductAction extends ProductUpdateInterface {
    type: typeof SALE_ADD_PRODUCT;
    product: SaleProduct;
}

export interface RemoveProductAction extends ProductUpdateInterface {
    type: typeof SALE_REMOVE_PRODUCT;
}

export interface ChangeQuantityAction {
    type: typeof SALE_CHANGE_QUANTITY;
    amount: number;
    absolute: boolean;
    index: number;
}

export interface ShowProductDetailsAction {
    type: typeof SALE_SHOW_PRODUCT_DETAILS;
    id: string;
    index: number | null;
}

export interface HideProductDetailsAction {
    type: typeof SALE_HIDE_PRODUCT_DETAILS;
}

export interface UpdateProductsAction {
    type: typeof SALE_UPDATE_PRODUCTS;
    products: Array<SaleProduct>;
}

export interface DiscountProductAction extends ProductUpdateInterface {
    type: typeof SALE_DISCOUNT_PRODUCT;
    indexAddress: Array<number>;
    amount: number;
    reason?: string;
}

export interface UnlockProductAction extends ProductUpdateInterface {
    type: typeof SALE_UNLOCK_PRODUCT;
    basePrice: number;
}

export type RequestAssociateBarcodeAction = {
    type: typeof SALE_REQUEST_ASSOCIATE_BARCODE;
    enabled: boolean;
    barcode: string | null;
};

export interface AddGiftCardAction {
    type: typeof SALE_ADD_GIFTCARD;
    product: SaleProduct;
}

export interface ToggleCaseAction extends ProductUpdateInterface {
    type: typeof SALE_TOGGLE_CASE;
}

export type SellScreenProductActionTypes =
    AddProductAction |
    RemoveProductAction |
    ChangeQuantityAction |
    ShowProductDetailsAction |
    HideProductDetailsAction |
    UpdateProductsAction |
    DiscountProductAction |
    UnlockProductAction |
    RequestAssociateBarcodeAction |
    AddGiftCardAction |
    ToggleCaseAction;

export function updateSaleProducts(products: Array<SaleProduct>): SellScreenActionTypes {
    return {
        type: SALE_UPDATE_PRODUCTS,
        products,
    };
}

export function requestQuantity(
    uuid: string,
    options: SaleAddProductOptions
): ShopfrontThunk<ShowDialogAction> {
    return (dispatch): Promise<void> => {
        // Get the product
        return determineDataSource()
            .then((source) => {
                return source.products.get(uuid);
            })
            .then((product) => {
                dispatch(showDialog(
                    "Quantity Requested",
                    <RequestQuantity
                        options={options}
                        uuid={uuid}
                        name={product.name}
                    />,
                    "question",
                    false,
                    false
                ));
            })
            .catch((err) => {
                console.error(
                    "An error occurred while requesting the quantity for a product (" + uuid + ")",
                    err,
                    options
                );
                PopupManager.error("We could not request the quantity for the product");
            });
    };
}

export function requestPrice(
    uuid: string,
    options: SaleAddProductOptions
): ShopfrontThunk<ShowDialogAction> {
    return (dispatch): Promise<void> => {
        // Get the product
        return determineDataSource()
            .then((source) => {
                return source.products.get(uuid);
            })
            .then((product) => {
                return dispatch(showDialog(
                    "Price Requested",
                    <RequestPrice
                        options={options}
                        uuid={uuid}
                        name={product.name}
                        type={product.type}
                    />,
                    "question",
                    false,
                    false
                ));
            })
            .catch((err) => {
                console.error(
                    "An error occurred while requesting the price for a product (" + uuid + ")",
                    err,
                    options
                );
                PopupManager.error("We could not request the price for the product");
            });
    };
}

export function showRequestWeight(
    callback: (weight: number) => void
): ReturnType<typeof showDialog> {
    return showDialog(
        "Weight Required",
        <RequestWeight
            callback={callback}
        />,
        "question",
        false,
        false
    );
}

export function requestWeight(
    uuid: string,
    options: SaleAddProductOptions
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        weigh(dispatch)
            .then((weight: number): void => {
                const quantity = typeof options.quantity !== "number" ? 0 : options.quantity;

                dispatch(addProductToSale(uuid, {
                    ...options,
                    quantity: weight * quantity,
                    weight  : false,
                }));
            });
    };
}

export function sendDeleteProductAction(
    product: SaleProduct
): void {
    if(product.type === "GiftCard") {
        return;
    }

    const state = store.getState();
    const register = state.location.register?.name ?? null;
    const outlet = state.location.outlet?.name ?? null;

    void sendAction("REMOVED_PRODUCT", "App\\Models\\Product", product.uuid, {
        quantity: Math.round(product.quantity * 100) / 100,
        price   : Math.round(product.prices.price * 100) / 100,
        register,
        outlet,
    });

    void sendSecurityEvent("REMOVED_PRODUCT", {
        id      : product.uuid,
        name    : product.name,
        quantity: Math.round(product.quantity * 100) / 100,
        price   : Math.round(product.prices.price * 100) / 100,
    });
}

async function releaseEnterpriseGiftCard(giftCard: SaleGiftCard): Promise<void> {
    let source;
    const sources = getPolicy("gift_card_sources", []);

    for(let i = 0, l = sources.length; i < l; i++) {
        if(sources[i].connection === giftCard.source) {
            source = sources[i].address;
            break;
        }
    }

    if(!source) {
        PopupManager.error("Gift card source is no longer available, we've removed the card from the sale anyway");

        return;
    }

    const response = await fetch(`${source}/${giftCard.code}`, {
        method : "DELETE",
        headers: {
            "x-shopfront-token" : await getEnterpriseAuthToken(giftCard.source),
            "x-shopfront-vendor": getVendorSubdomain(),
            "x-shopfront-host"  : getMainHost(),
            "accept"            : "application/json",
        },
    });

    const body = await response.json();

    if(response.status !== 200) {
        console.error("Unable to release a gift card", body);
        let message;

        if(body.error && body.message) {
            message = body.message;
        } else {
            message = "Unknown Error";
        }

        PopupManager.error(
            `Unable to release gift card, response from Enterprise: ${
                message
            }. The card has still been removed from the sale`
        );
    }
}

export function removeProductFromSale(
    indexAddress: Array<number>,
    clear = true,
    changeActiveIndex = true
): ShopfrontThunk<SellScreenActionTypes> {
    console.log("Removing a product from the sale", indexAddress);

    return (dispatch, getState): void => {
        const sale = getState().sellscreen.sale;

        if(sale.products.length === 1 && sale.totals.paid && indexAddress.length === 1) {
            console.log("Payments are on the sale, not removing the product");
            PopupManager.warning("Remove all payments before you remove the last product");
            dispatch(changeSellScreenActionMode("payment"));
            dispatch(changeSellScreenSummaryMode("payments"));
        } else {
            dispatch(setNextSearchAction(false));

            // Send product deleted event only if it is a top level component
            if(indexAddress.length === 1) {
                const product: SaleProduct = sale.products[indexAddress[0]];

                console.log(`Removing product: ${product.uuid}`);

                sendDeleteProductAction(product);

                // Release the gift card if required
                if(product.giftCard && product.giftCard.source !== "internal") {
                    void releaseEnterpriseGiftCard(product.giftCard);
                }
            }

            if(sale.products.length === 1 && !sale.customer && clear && indexAddress.length === 1) {
                console.log("Not items left on the sale, clearing");
                document.dispatchEvent(new CustomEvent(SALE_TRANSACTION_FINISHED, {
                    detail: false,
                }));
                dispatch(clearSale());
            } else {
                dispatch({
                    type: SALE_REMOVE_PRODUCT,
                    indexAddress,
                });

                // Select the previous index if it has been deleted
                if(changeActiveIndex && indexAddress.length === 1 && sale.products.length - 1 === indexAddress[0]) {
                    dispatch(changeActiveProductIndex(-1, false));
                }
            }
        }
    };
}

export function changeProductQuantity(
    index: number,
    amount: number,
    absolute = true
): ShopfrontThunk<ChangeQuantityAction> {
    return (dispatch, getState): void => {
        const state = getState();

        if(!absolute) {
            const product = state.sellscreen.sale.products[index];
            const quantity = product.quantity;

            if(quantity > 0 && quantity + amount <= 0) {
                amount = product.isCase ? product.caseQuantity : 1;
                absolute = true;
            } else if(quantity < 0 && quantity + amount >= 0) {
                amount = product.isCase ? -product.caseQuantity : -1;
                absolute = true;
            }
        }

        dispatch(changeSellScreenSummaryMode("transaction"));
        dispatch({
            type: SALE_CHANGE_QUANTITY,
            index,
            amount,
            absolute,
        });
    };
}

export function toggleCase(
    indexAddress: Array<number>
): SellScreenActionTypes {
    return {
        type: SALE_TOGGLE_CASE,
        indexAddress,
    };
}

export function flipSaleProducts(): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        dispatch(startLoading());

        Permissions.can("refund")
            .then(permitted => {
                if(permitted) {
                    const products = getState().sellscreen.sale.products;

                    for(let i = 0, l = products.length; i < l; i++) {
                        const quantity = products[i].quantity * -1;

                        dispatch(changeProductQuantity(i, quantity));
                    }
                } else {
                    PopupManager.error("You do not have permission to refund products");
                }
            })
            .catch(err => {
                console.error("Error retuning sale product", err);
                PopupManager.error("Failed to return sale product");
            })
            .finally(() => {
                dispatch(stopLoading());
            });
    };
}

export function returnSaleProduct(
    index: number
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        dispatch(startLoading());

        Permissions.can("refund")
            .then(permitted => {
                if(permitted) {
                    const state = getState();
                    const product = state.sellscreen.sale.products[index];

                    if(!product) {
                        return;
                    }

                    if(product.giftCard) {
                        PopupManager.error("You cannot return a gift card");

                        return;
                    }

                    const quantity = product.quantity * -1;

                    if(quantity < 0) {
                        PopupManager.warning(
                            "Negative products are returned at the everyday price, you may need to adjust the price" +
                            " or return the product via the sales history"
                        );
                    } else {
                        if(state.sellscreen.sale.refundReason) {
                            let hasNegative = false;

                            for(let i = 0, l = state.sellscreen.sale.products.length; i < l; i++) {
                                if(i === index) {
                                    continue;
                                }

                                if(state.sellscreen.sale.products[i].quantity < 0) {
                                    hasNegative = true;
                                    break;
                                }
                            }

                            if(!hasNegative) {
                                dispatch(addRefundReason(""));
                            }
                        }
                    }

                    dispatch(changeProductQuantity(index, quantity));
                } else {
                    PopupManager.error("You do not have permission to refund products");
                }
            })
            .catch(err => {
                console.error("Error retuning sale product", err);
                PopupManager.error("Failed to return sale product");
            })
            .finally(() => {
                dispatch(stopLoading());
            });
    };
}

export function showProductDetails(
    id: string,
    index: number | null,
    showComponents = false,
    changeActionMode = true
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch({
            type: SALE_SHOW_PRODUCT_DETAILS,
            id,
            index,
        });

        dispatch(changeSellScreenSummaryMode("product-details"));

        if(changeActionMode) {
            if(showComponents) {
                dispatch(changeSellScreenActionMode("components"));
            } else {
                dispatch(changeSellScreenActionMode("keys"));
            }
        }
    };
}

export function hideProductDetails(
    changeActionMode = true
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch({
            type : SALE_HIDE_PRODUCT_DETAILS,
            id   : false,
            index: false,
        });

        if(changeActionMode) {
            dispatch(changeSellScreenActionMode("keys"));
        }

        dispatch(changeSellScreenSummaryMode("transaction"));
    };
}

const discountProductsByDistributing = (
    products: Array<SaleProduct>,
    discountTo: number,
    reason: string | undefined,
    dispatch: ShopfrontDispatch,
    parentIndexAddress: Array<number> = []
): void => {
    let originalTotal = 0;
    let preventedTotal = 0;
    let firstPrevented = null;
    let hasMultiplePrevented = false;

    for(const product of products) {
        if(product.preventManualDiscount) {
            if(firstPrevented) {
                hasMultiplePrevented = true;
            } else {
                firstPrevented = product.name;
            }

            preventedTotal += (product.prices ? product.prices.price : 0);
        } else {
            originalTotal += (product.prices ? product.prices.price : 0);
        }
    }

    if(!originalTotal) {
        PopupManager.warning("Could not discount the sale, please discount the items individually");

        return;
    }

    if(preventedTotal) {
        discountTo = Math.round((discountTo - preventedTotal) * 100) / 100;
    }

    if(discountTo <= 0 && preventedTotal > 0) {
        if(parentIndexAddress.length && firstPrevented) {
            PopupManager.warning(
                `${firstPrevented}${hasMultiplePrevented ? " and others have" : " has"} disabled manual ` +
                `discounts, please discount the item${hasMultiplePrevented ? "s" : ""} individually. The ` +
                "discount total might need to be recalculated"
            );
        } else {
            PopupManager.warning(
                "One or more products has disabled manual discounts, please discount the " +
                "items individually"
            );
        }

        return;
    }

    let total = 0;
    let rounding = 0;

    let hasPrevented = false;

    for(let i = 0, l = products.length; i < l; i++) {
        const product = products[i];

        if(product.preventManualDiscount) {
            hasPrevented = true;
            continue;
        }

        const original = product.prices.price;

        const originalPercentage = original / originalTotal;
        const newAmount = originalPercentage * discountTo;
        let rounded = Math.round(newAmount * 100) / 100;

        total += newAmount;
        rounding += rounded;

        const difference = total - rounding;

        if(Math.abs(difference) > 0.01) {
            const roundedDiff = Math.round(difference * 100) / 100;

            rounding += roundedDiff;
            rounded += roundedDiff;
        }

        if(i + 1 === l) {
            if(rounding !== discountTo) {
                rounded += discountTo - rounding;
            }
        }

        if(product.products.length) {
            const childTotal = product.prices.additionalPrice[1];
            const fullPrice = product.prices.price;

            const childDiscountedAmount = fullPrice > 0 ?
                Math.round((rounded * (childTotal / fullPrice)) * 100) / 100 :
                0;

            discountProductsByDistributing(
                product.products,
                childDiscountedAmount,
                undefined,
                dispatch,
                [ ...parentIndexAddress, i ]
            );

            rounded -= childDiscountedAmount;
        }

        dispatch(discountProduct(
            [ ...parentIndexAddress, i ],
            rounded,
            reason,
            true
        ));
    }

    if(hasPrevented) {
        PopupManager.warning(
            "One or more products has disabled manual discounts, the discount total might need to " +
            "be recalculated"
        );
    }
};

export function discountProduct(
    indexAddress: Array<number>,
    amount: number | string,
    reason?: string,
    ignoreDistribution = false
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState) => {
        let discount = typeof amount === "string" ? parseFloat(amount) : amount;

        const state = getState();
        let currentProduct = null;

        for(const index of indexAddress) {
            if(currentProduct === null) {
                currentProduct = state.sellscreen.sale.products[index];
            } else {
                currentProduct = currentProduct.products[index];
            }
        }

        if(currentProduct === null) {
            throw new Error(`Could not determine the product at index ${indexAddress.join(".")}`);
        }

        if(indexAddress.length === 1) {
            const surcharge = currentProduct.surcharge;

            let rule: null | SaleSurcharge = null;

            if(surcharge) {
                rule = state.sellscreen.sale.surchargeRules.find(saleSurcharge => {
                    return saleSurcharge.id === surcharge.id;
                }) ?? null;
            }

            if(rule) {
                if(rule.type === "FIXED") {
                    discount -= rule.amount;
                } else {
                    discount = discount / (1 + (rule.amount / 100));
                }

                discount = Math.round(discount * 10000) / 10000;

                if(discount <= 0) {
                    PopupManager.warning(
                        "This discount will cause the product to go bellow zero, due to the surcharge on the " +
                        "product"
                    );

                    return;
                }
            }
        }

        if(currentProduct.products.length && !ignoreDistribution) {
            const childTotal = currentProduct.prices.additionalPrice[1];
            const fullPrice = currentProduct.prices.price;

            const childDiscountedAmount = fullPrice > 0 ?
                Math.round((discount * (childTotal / fullPrice)) * 100) / 100 :
                0;

            discountProductsByDistributing(
                currentProduct.products,
                childDiscountedAmount,
                undefined,
                dispatch,
                indexAddress
            );

            discount -= childDiscountedAmount;
        }

        dispatch({
            type  : SALE_DISCOUNT_PRODUCT,
            amount: discount,
            indexAddress,
            reason,
        });
    };
}

export function discountSale(
    amount: number,
    reason: string
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch, getState): void => {
        const state = getState();

        amount = Math.round(amount * 100) / 100;

        const products: Array<SaleProduct> = state.sellscreen.sale.products;

        if(state.sellscreen.sale.products.some(product => product.surcharge !== null)) {
            PopupManager.warning(
                "One or more products are affected by surcharges, please discount the " +
                "items individually"
            );

            return;
        }

        const activeSaleSurcharge = state.sellscreen.sale.surcharge;

        if(activeSaleSurcharge?.amount) {
            const surchargeRule = state.sellscreen.sale.surchargeRules.find(rule => rule.id === activeSaleSurcharge.id);

            if(surchargeRule) {
                if(surchargeRule.type === "FIXED") {
                    amount -= surchargeRule.amount;
                } else {
                    amount = amount / (1 + (surchargeRule.amount / 100));
                }

                amount = Math.round(amount * 10000) / 10000;

                if(amount <= 0) {
                    PopupManager.warning("Cannot discount to bellow the surcharge amount");

                    return;
                }
            }
        }

        discountProductsByDistributing(products, amount, reason, dispatch);
    };
}

export function unlockProduct(
    indexAddress: Array<number>
): ShopfrontThunk<SellScreenActionTypes> {
    return async (dispatch, getState): Promise<void> => {
        const state = getState();
        let item = state.sellscreen.sale.products[indexAddress[0]];

        for(let i = 1, l = indexAddress.length; i < l; i++) {
            item = item.products[indexAddress[i]];
        }

        dispatch({
            type     : SALE_UNLOCK_PRODUCT,
            basePrice: await PromotionAgent.getAgent().everydayPrice(item.uuid, 1).catch(() => 0),
            indexAddress,
        });
    };
}

export function requestAssociateProduct(
    barcode: string
): ShopfrontThunk<SellScreenActionTypes> {
    return (dispatch): void => {
        dispatch({
            type   : SALE_REQUEST_ASSOCIATE_BARCODE,
            enabled: true,
            barcode,
        });
        dispatch(changeSellScreenSearch("[Clear]"));
        dispatch(changeSellScreenActionMode("search", {
            clearBarcode: false,
        }));
    };
}

export function clearAssociateBarcode(): AnyAction {
    return {
        type   : SALE_REQUEST_ASSOCIATE_BARCODE,
        enabled: false,
        barcode: "",
    };
}

export function formatGiftCard(
    code: string,
    amount: number | string,
    expiry: string,
    user: string,
    source: string
): SaleProduct {
    const parsedAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    return {
        lineId      : UUID.generate(),
        uuid        : UUID.generate(),
        type        : "GiftCard",
        name        : "Gift Card (" + code + ")",
        caseQuantity: 1,
        isCase      : false,
        quantity    : 1,
        loyalty     : {
            earn  : 0,
            redeem: 0,
        },
        prices: {
            price          : parsedAmount,
            unlockedPrice  : parsedAmount,
            normal         : 0,
            base           : 0,
            addPrice       : 0,
            removePrice    : 0,
            additionalPrice: [ 0, 0 ],
        },
        tax: {
            id    : false,
            amount: 0,
        },
        note    : "",
        products: [],
        giftCard: {
            code  : code,
            amount: parsedAmount,
            expiry: expiry,
            source,
        },
        surcharge            : null,
        preventManualDiscount: true,
        special              : false,
        edited               : true,
        promotions           : {},
        userId               : user,
        canUnlock            : false,
        defaultProducts      : [],
        discountReason       : "",
        familyId             : null,
        categoryId           : null,
        tags                 : [],
        priceList            : false,
        rebates              : {},
        lockQuantity         : true,
        metaData             : {},
    };
}

export type SaleAddGiftCardOptions = {
    code: string | "auto";
    amount: string | number | "auto";
    expiry: string | "auto";
    returnToSale: boolean;
    source: string | "auto";
};

export interface GiftCardPaymentRequest {
    method: string;
    amount: number;
    options: SaleAddPaymentOptions;
}

export function requestGiftCardSource(
    options: SaleAddGiftCardOptions | GiftCardPaymentRequest
): ReturnType<typeof showDialog> {
    return showDialog(
        "Gift Card Source",
        <RequestGiftCardSource {...options} />,
        "question",
        false,
        false
    );
}

export function requestCode(
    options: SaleAddGiftCardOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Gift Card Code",
        <RequestCode {...options} />,
        "question",
        false,
        false
    );
}

export function requestAmount(
    options: SaleAddGiftCardOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Gift Card Value",
        <RequestAmount {...options} />,
        "question",
        false,
        false
    );
}

export function requestExpiry(
    options: SaleAddGiftCardOptions
): ReturnType<typeof showDialog> {
    return showDialog(
        "Gift Card Expiry",
        <RequestExpiry {...options} />,
        "question",
        false,
        false
    );
}

const reserveEnterpriseGiftCard = async (options: SaleAddGiftCardOptions): Promise<void> => {
    const sources = getPolicy("gift_card_sources", []);

    let source;

    for(let i = 0, l = sources.length; i < l; i++) {
        if(sources[i].connection === options.source) {
            source = sources[i];
            break;
        }
    }

    if(typeof source === "undefined") {
        throw new Error("Could not find gift card source");
    }

    const response = await fetch(`${source.address}/reserve`, {
        method : "POST",
        headers: {
            "x-shopfront-token" : await getEnterpriseAuthToken(source.connection),
            "x-shopfront-vendor": getVendorSubdomain(),
            "x-shopfront-host"  : getMainHost(),
        },
        body: JSON.stringify({
            code  : options.code,
            amount: options.amount,
            expiry: options.expiry,
        }),
    });

    if(response.status !== 200) {
        throw new Error("Invalid response returned from enterprise when reserving a gift card");
    }
};

export function addGiftCardToSale(
    partialOptions: Partial<SaleAddGiftCardOptions>
): ShopfrontThunk<SellScreenActionTypes> | NoopActionType {
    if(!Subscription.can(Subscription.Features.GIFT_CARDS)) {
        PopupManager.error("Gift Cards aren't available on your current subscription");

        return NoopAction;
    }

    const enterpriseSources = getPolicy("gift_card_sources", []);

    if(!getOption("outlet_enable_gift_cards") && enterpriseSources.length === 0) {
        PopupManager.error("Gift cards are not enabled for this Outlet");

        return NoopAction;
    }

    const defaultOptions: SaleAddGiftCardOptions = {
        code        : "auto",
        amount      : "auto",
        expiry      : "auto",
        returnToSale: false,
        source      : "auto",
    };

    const options: SaleAddGiftCardOptions = Object.assign({}, defaultOptions, partialOptions);

    return async (dispatch, getState): Promise<void> => {
        // Handle setting options
        if(options.source === "auto") {
            if(enterpriseSources.length) {
                if(enterpriseSources.length === 1 && !getOption("outlet_enable_gift_cards")) {
                    options.source = enterpriseSources[0].connection;
                } else {
                    return dispatch(requestGiftCardSource(options));
                }
            } else {
                options.source = "internal";
            }
        }

        if(options.code === "auto") {
            return dispatch(requestCode(options));
        }

        if(options.amount === "auto") {
            return dispatch(requestAmount(options));
        }

        if(options.expiry === "auto") {
            return dispatch(requestExpiry(options));
        }

        const state = getState();
        const user = state.user.id;

        if(user === false) {
            PopupManager.error("There is no user currently logged in");

            return;
        }

        if(!state.sellscreen.sale.products.length) {
            // Get the surcharge rules
            getSurchargeRules()
                .then(rules => {
                    dispatch(setSaleSurchargingRules(rules));
                });
        }

        if(options.source !== "internal") {
            dispatch(startLoading());
            try {
                await reserveEnterpriseGiftCard(options);
            } catch(e) {
                console.error("We could not reserve an enterprise gift card", e);
                PopupManager.error("We were unable to reserve the gift card, please try again");
                dispatch(stopLoading());

                return;
            }

            dispatch(stopLoading());
        }

        dispatch({
            type   : SALE_ADD_GIFTCARD,
            product: formatGiftCard(options.code, options.amount, options.expiry, user, options.source),
        });

        if(options.returnToSale) {
            // Change active index to new product
            dispatch(changeSellScreenSummaryMode("transaction"));
            dispatch(changeActiveProductIndex(state.sellscreen.sale.products.length));
        }
    };
}

async function customiseIntegratedProduct(
    product: SaleProduct,
    dispatch: ShopfrontDispatch
): Promise<SaleProduct> {
    if(product.type !== "Integrated") {
        return product;
    }

    dispatch(toggleAddingIntegratedProduct(true));
    const response = await getApplicationManager().sendPipeline(
        ApplicationEventsFromShopfront.FORMAT_INTEGRATED_PRODUCT,
        {
            product,
        },
        {}
    );

    dispatch(toggleAddingIntegratedProduct(false));

    return response.product;
}

export async function formatSaleProduct(
    uuid: string,
    partialOptions: Partial<SaleAddProductOptions> | null,
    dispatch: ShopfrontDispatch,
    user: string,
    parent: null | SaleProduct = null
): Promise<SaleProduct | void> {
    const defaultOptions: SaleAddProductOptions = {
        quantity       : "auto",
        price          : "auto",
        weight         : "auto",
        consolidate    : "auto",
        isCase         : "auto",
        barcodeQuantity: 1,
        deleted        : false,
        edited         : false,
        indexAddress   : [],
        returnToSale   : false,
        metaData       : {},
        lockQuantity   : false,
    };

    const options = Object.assign({}, defaultOptions, partialOptions) as SaleAddProductOptions;

    if(options.weight === true) {
        options.weight = "request";
    }

    // Get the data source
    const source = await determineDataSource();

    // Get the product
    let product: LocalDatabaseProduct;

    try {
        product = await source.products.get(uuid);
    } catch(err) {
        console.error(`An error occurred while retrieving a product to format for the sale (${uuid})`, err, options);
        PopupManager.error("Unable to find the product to add to the sale");

        return;
    }

    let barcodeQuantity = 1;

    // Check if the product has special requirements
    if(options.quantity === "request" || (options.quantity === "auto" && product.request_quantity)) {
        // Set barcode quantity to default
        if(options.barcodeQuantity === "auto") {
            options.barcodeQuantity = 1;
        }

        // Request the quantity
        dispatch(requestQuantity(uuid, options));

        return;
    } else if(options.quantity === "auto") {
        if(
            (
                typeof options.barcodeQuantity === "undefined" ||
                options.barcodeQuantity === "auto"
            ) &&
            options.isCase === true
        ) {
            options.quantity = 1;
            options.barcodeQuantity = product.case_quantity;
        } else if(options.barcodeQuantity === "auto") {
            // Find the minimum price point of the product
            let minQuantity = 1;

            for(let i = 0, l = product.prices.length; i < l; i++) {
                if(i === 0) {
                    minQuantity = product.prices[i].quantity;
                } else {
                    minQuantity = Math.min(minQuantity, product.prices[i].quantity);
                }
            }

            options.barcodeQuantity = minQuantity;
            options.quantity = minQuantity;
        } else {
            options.quantity = options.barcodeQuantity;
            barcodeQuantity = options.barcodeQuantity;
        }
    }

    let priceRequested = product.request_price || product.prices.length === 0;

    if(options.price === "request" || (options.price === "auto" && priceRequested)) {
        // Request the price
        dispatch(requestPrice(uuid, options));

        return;
    }

    if(options.weight === "request" || (options.weight === "auto" && product.use_scales)) {
        // Request the weight
        dispatch(requestWeight(uuid, options));

        return;
    }

    if(
        options.isCase === "auto" &&
        product.case_quantity !== 1 &&
        options.quantity % product.case_quantity === 0
    ) {
        options.isCase = true;
        options.quantity = options.quantity / product.case_quantity;
    } else if(typeof options.isCase !== "boolean") {
        options.isCase = false;
    }

    if(options.isCase) {
        // Temporary adjustment for quantity
        options.quantity = options.quantity * product.case_quantity;
    }

    // Get any additional products
    const containedProducts = [];

    if(Array.isArray(product.components) && product.components.length) {
        for(let i = 0, l = product.components.length; i < l; i++) {
            if(product.components[i].quantity !== 0) {
                let component;

                try {
                    component = await formatSaleProduct(
                        product.components[i].uuid,
                        {
                            quantity: product.components[i].quantity,
                            price   : 0,
                            weight  : false,
                        },
                        dispatch,
                        user
                    );

                    if(component) {
                        component.prices.removePrice = product.components[i].remove_price;

                        delete component.requestPrice;
                    }
                } catch(e) {
                    console.error(
                        `An error occurred while adding additional products for the sale (${uuid} -> ${
                            product.components[i].uuid
                        })`,
                        e,
                        options
                    );
                    PopupManager.error("Unable to add a component within the product to the sale");

                    return;
                }

                if(component) {
                    containedProducts.push(component);
                }
            }
        }
    }

    const type = uppercaseFirst(product.type);

    let hasAutoPrice = false;

    if(typeof options.price === "number") {
        priceRequested = true;
    } else if(options.price === "auto") {
        hasAutoPrice = true;
        options.price = 0;
    }

    let addPrice = 0;
    let removePrice = 0;

    if(parent !== null) {
        const dbParent = await source.products.get(parent.uuid);
        let found = false;

        for(let i = 0, l = dbParent.components.length; i < l; i++) {
            if(dbParent.components[i].uuid === uuid) {
                found = true;
                addPrice = dbParent.components[i].add_price;
                removePrice = dbParent.components[i].remove_price;
                break;
            }
        }

        if(!found) {
            // Use the single everyday price as the default add price
            addPrice = hasAutoPrice ?
                await PromotionAgent.getAgent().everydayPrice(uuid, 1).catch(() => 0) :
                options.price;
        }
    }

    // Dispatch the add product event
    return customiseIntegratedProduct({
        lineId      : UUID.generate(),
        uuid        : product.uuid,
        type        : type,
        name        : product.name,
        caseQuantity: product.case_quantity < 1 ? 1 : product.case_quantity,
        isCase      : options.isCase,
        quantity    : options.quantity,
        surcharge   : null,
        loyalty     : {
            earn  : 0,
            redeem: 0,
        },
        prices: {
            price        : options.price,
            unlockedPrice: options.price,
            normal       : (priceRequested ? options.price : 0),
            base         : (
                priceRequested ?
                    options.price :
                    await PromotionAgent.getAgent().everydayPrice(product.uuid, 1).catch(() => 0)
            ),
            additionalPrice: [ 0, 0 ],
            addPrice,
            removePrice,
        },
        tax: {
            id    : product.tax_rate ? product.tax_rate.uuid : false,
            amount: product.tax_rate ? parseFloat(product.tax_rate.amount) : 0,
        },
        note                 : "",
        products             : containedProducts,
        defaultProducts      : containedProducts,
        special              : product.request_price || priceRequested,
        edited               : options.edited,
        preventManualDiscount: product.prevent_manual_discounts,
        promotions           : {},
        rebates              : {},
        userId               : user,
        deleted              : options.deleted,
        initiallyDeleted     : options.deleted,
        familyId             : product.family_id,
        categoryId           : product.category_id,
        tags                 : product.tags,
        canUnlock            : !priceRequested,
        requestPrice         : priceRequested,
        lockQuantity         : options.lockQuantity,
        metaData             : options.metaData,
        giftCard             : null,
        priceList            : false,
        barcodeQuantity      : barcodeQuantity,
    }, dispatch);
}

function markProductAsAdding(id: number | null): MarkProductAsAddingToSale {
    return {
        type: MARK_PRODUCT_AS_ADDING,
        id,
    };
}

function toggleAddingIntegratedProduct(enabled: boolean): AddingIntegratedProductToSale {
    return {
        type: ADD_INTEGRATED_PRODUCT_TO_SALE,
        enabled,
    };
}

export async function getSurchargeRules(): Promise<Array<SaleSurcharge>> {
    const registerId = getOption("register");

    if(!registerId) {
        return [];
    }

    const source = await determineDataSource();
    const register = await source.registers.get(registerId);

    if(!register.surcharging_enabled || !register.surcharges) {
        return [];
    }

    const currentTime = moment();
    const dayOfWeek = moment.weekdays()[currentTime.day()].toUpperCase();

    const results: Array<SaleSurcharge> = [];

    for(let i = 0, l = register.surcharges.length; i < l; i++) {
        if(register.surcharges[i].day !== dayOfWeek) {
            continue;
        }

        if(
            currentTime.isBetween(
                `${currentTime.format("YYYY-MM-DD")} ${register.surcharges[i].start}`,
                `${currentTime.format("YYYY-MM-DD")} ${register.surcharges[i].end}`
            )
        ) {
            results.push({
                id       : register.surcharges[i].id,
                amount   : register.surcharges[i].amount,
                type     : register.surcharges[i].type,
                taxRate  : register.surcharges[i].taxRate,
                name     : register.surcharges[i].name,
                appliesTo: register.surcharges[i].appliesTo,
            });
        }
    }

    return results;
}

/**
 * Add a product to the current sale
 * @param uuid
 * @param partialOptions
 * @returns {Function}
 */
export function addProductToSale(
    uuid: string,
    partialOptions?: Partial<SaleAddProductOptions>
): ShopfrontThunk<SellScreenActionTypes> {
    const defaultOptions: SaleAddProductOptions = {
        quantity       : "auto",
        barcodeQuantity: "auto",
        price          : "auto",
        weight         : "auto",
        consolidate    : "auto",
        isCase         : "auto",
        indexAddress   : [],
        returnToSale   : true,
        deleted        : false,
        edited         : false,
        metaData       : {},
        lockQuantity   : false,
    };

    const options: SaleAddProductOptions = Object.assign({}, defaultOptions, partialOptions);

    if(process.env.NODE_ENV !== "test") {
        if(getLogLevel() >= LogLevel.NORMAL) {
            console.log(`Adding a product to the sale: ${uuid}`, options);
        } else {
            console.log(`Adding a product to the sale: ${uuid}`);
        }
    }

    return async (dispatch, getState): Promise<void> => {
        // Mark the product as adding and clear the search
        const addToSaleId = Date.now();

        if(process.env.NODE_ENV !== "test") {
            console.log(`Marking product ${uuid} as adding to sale with add to sale ID`, addToSaleId);
        }

        if(!getState().sellscreen.sale.products.length) {
            // Get the surcharge rules
            getSurchargeRules()
                .then(rules => {
                    dispatch(setSaleSurchargingRules(rules));
                });
        }

        batch(() => {
            dispatch(markProductAsAdding(addToSaleId));
            dispatch(changeSellScreenSearch("[Clear]"));
        });

        const state = getState();
        const user = state.user.id as string;

        let parent: null | SaleProduct = null;

        for(let i = 0, l = options.indexAddress.length; i < l; i++) {
            if(parent === null) {
                parent = state.sellscreen.sale.products[options.indexAddress[i]];
            } else {
                parent = parent.products[options.indexAddress[i]];
            }
        }

        // Get the product
        let results;

        try {
            results = await formatSaleProduct(uuid, options, dispatch, user, parent);
        } catch(err) {
            console.error(`An error occurred while formatting a product (${uuid}) to add to the sale`, err, options);
            PopupManager.error("We could not add the product to the sale");

            if(getState().sellscreen.sale.addingToSale === addToSaleId) {
                dispatch(markProductAsAdding(null));
            }

            return;
        }

        // Product will return null if something has been requested (Price, Weight)
        if(!results) {
            if(getState().sellscreen.sale.addingToSale === addToSaleId) {
                dispatch(markProductAsAdding(null));
            }

            return;
        }

        const { requestPrice, ...product } = results;

        // Check if we should consolidate the product or not
        let consolidate = options.consolidate;

        if(consolidate === "auto") {
            if(results.type === "Package" || results.lockQuantity) {
                // Do not consolidate packages
                consolidate = false;
            } else {
                consolidate = getOption("register_consolidate_sale_lines");
            }
        }

        if(consolidate && !requestPrice && options.indexAddress.length === 0) {
            // Check if the product already exists in the sale
            const products = state.sellscreen.sale.products;
            let found: false | number = false;

            for(let i = 0, l = products.length; i < l; i++) {
                if(products[i].uuid === product.uuid && products[i].isCase === product.isCase) {
                    found = i;
                    break;
                }
            }

            if(found !== false) {
                batch(() => {
                    dispatch(changeProductQuantity(found as number, product.quantity, false));

                    if(getState().sellscreen.sale.addingToSale === addToSaleId) {
                        dispatch(markProductAsAdding(null));
                    }

                    dispatch(changeSellScreenSummaryMode("transaction"));
                });

                return;
            }
        }

        // Not consolidating or product not in the sale
        await batch(() => {
            dispatch({
                product,
                type        : SALE_ADD_PRODUCT,
                indexAddress: options.indexAddress,
            });

            // Change active index to new product
            const index = state.sellscreen.sale.products.length;

            dispatch(changeActiveProductIndex(index));

            if(options.returnToSale) {
                // Return to sell screen
                dispatch(changeSellScreenSummaryMode("transaction"));
                dispatch(changeSellScreenActionMode("keys"));

                if(getOption("find_on_lookup")) {
                    if(getState().sellscreen.sale.addingToSale === addToSaleId) {
                        dispatch(markProductAsAdding(null));
                    }

                    return Permissions.can("view_product")
                        .then(can => {
                            if(can) {
                                dispatch(showProductDetails(uuid, index, product.products.length > 0));
                            }
                        });
                }
            }

            if(getState().sellscreen.sale.addingToSale === addToSaleId) {
                dispatch(markProductAsAdding(null));
            }
        });
    };
}
